import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
    House,
    Cow,
    Grains,
    Scales,
    Drop,
    ClipboardText,
    Gear,
    SignOut,
    CircleNotch,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { Toaster } from "@/components/ui/sonner";
import { Logo } from "@/components/Logo";

export default function Layout() {
    const { user, logout } = useAuth();
    const { t, lang, setLang } = useT();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    const navItems = [
        { to: "/", label: t("nav_dashboard"), icon: House, testid: "nav-dashboard" },
        { to: "/animales", label: t("nav_animals"), icon: Cow, testid: "nav-animals" },
        { to: "/alimento", label: t("nav_feed"), icon: Grains, testid: "nav-feed" },
        { to: "/pesajes", label: t("nav_weighings"), icon: Scales, testid: "nav-weighings" },
        { to: "/leche", label: t("nav_milk"), icon: Drop, testid: "nav-milk" },
        { to: "/reportes", label: t("nav_reports"), icon: ClipboardText, testid: "nav-reports" },
        { to: "/ajustes", label: t("nav_settings"), icon: Gear, testid: "nav-settings" },
    ];

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar */}
            <aside
                className="hidden lg:flex flex-col w-64 shrink-0 bg-white text-[#083D1E] border-r border-border sticky top-0 h-screen"
                data-testid="app-sidebar"
            >
                <div className="px-5 py-6 border-b border-border">
                    <Logo className="h-14" />
                </div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === "/"}
                            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
                            data-testid={item.testid}
                        >
                            <item.icon size={18} weight="regular" />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>

                {/* Language switcher */}
                <div className="px-4 pb-3">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">{t("language")}</div>
                    <div className="flex gap-1 rounded-md bg-secondary p-1" data-testid="lang-switcher">
                        {["es", "en"].map((l) => (
                            <button
                                key={l}
                                onClick={() => setLang(l)}
                                className={`flex-1 text-xs font-medium py-1.5 rounded transition-colors ${lang === l ? "bg-[#083D1E] text-white" : "text-[#083D1E]/70 hover:text-[#083D1E]"}`}
                                data-testid={`lang-${l}`}
                            >
                                {l === "es" ? "Español" : "English"}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="p-4 border-t border-border">
                    <div className="text-xs text-muted-foreground mb-2">{t("session")}</div>
                    <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name || user?.email}</div>
                    <div className="text-xs text-muted-foreground truncate mb-3" data-testid="user-email">{user?.email}</div>
                    <button
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 text-sm py-2 rounded-md bg-secondary hover:bg-[#083D1E] hover:text-white transition-colors"
                        data-testid="logout-btn"
                    >
                        <SignOut size={16} /> {t("logout")}
                    </button>
                </div>
            </aside>

            {/* Mobile header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b border-border text-[#083D1E] px-4 py-2.5 flex items-center justify-between">
                <Logo className="h-10" />
                <div className="flex items-center gap-2">
                    <select
                        value={lang}
                        onChange={(e) => setLang(e.target.value)}
                        className="bg-secondary text-xs rounded px-2 py-1 border border-border"
                        data-testid="lang-switcher-mobile"
                    >
                        <option value="es">ES</option>
                        <option value="en">EN</option>
                    </select>
                    <button
                        onClick={handleLogout}
                        className="text-xs flex items-center gap-1 bg-secondary px-3 py-1.5 rounded-md"
                        data-testid="logout-btn-mobile"
                    >
                        <SignOut size={14} /> {t("exit")}
                    </button>
                </div>
            </div>

            {/* Mobile bottom nav */}
            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#083D1E] text-white grid grid-cols-7 py-1.5 border-t border-white/10" data-testid="mobile-bottom-nav">
                {navItems.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/"}
                        className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[9px] leading-tight px-0.5 py-1 min-w-0 ${isActive ? "text-[#82B401]" : "text-white/70"}`}
                        data-testid={`m-${item.testid}`}
                    >
                        <item.icon size={18} />
                        <span className="truncate max-w-full">{item.label}</span>
                    </NavLink>
                ))}
            </div>

            {/* Main content */}
            <main className="flex-1 min-w-0 lg:pt-0 pt-16 pb-20 lg:pb-0">
                <div className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-10 animate-fade-in">
                    <Outlet />
                </div>
            </main>

            <Toaster position="top-right" />
        </div>
    );
}

export function PageLoading() {
    return (
        <div className="flex items-center gap-2 text-muted-foreground">
            <CircleNotch className="animate-spin" size={18} /> Cargando…
        </div>
    );
}
